// react
import { useState } from 'react';

// internationalization
import { useTranslation } from 'react-i18next';

// tanstack
import { createFileRoute, Link, useParams } from '@tanstack/react-router';

// material ui icons
import AddRoundedIcon from '@mui/icons-material/AddRounded';

// api
import { useGetEforsyningMetadataIntegrationsEforsyningMetadataGetQuery } from '@/api/generated/dataIntegrations/endpoints/dataIntegrations';

// shared components
import { WritableOnly } from '@/components/access-control';
import PageContainer from '@/components/layout/PageContainer';
import PageHeader from '@/components/layout/PageHeader';
import { SubmitButton } from '@/components/ui/button/SubmitButton';
import LinearLoadingIndicator from '@/components/ui/progress/LinearLoadingIndicator';

// feature-components
import DeleteEforsyningIntegrationDialog from '@/features/eforsyning-integration/components/DeleteEforsyningIntegrationDialog';
import EforsyningIntegrationDataGrid from '@/features/eforsyning-integration/components/EforsyningIntegrationDataGrid';

export const Route = createFileRoute(
  '/$accountId/_user/eforsyning-integration/',
)({
  component: RouteComponent,
});

type EforsyningIntegrationListItem = {
  PartitionKey?: string | number;
  RowKey?: string;
  IntegrationId?: string | number;
  EforsyningIntegrationId?: string | number;
  Id?: string | number;
  Name?: string;
};

function RouteComponent() {
  const { t } = useTranslation('eforsyning-integration');
  const { accountId } = useParams({
    from: '/$accountId/_user/eforsyning-integration/',
  });
  const accountIdAsNumber = Number(accountId);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [integrationToDelete, setIntegrationToDelete] =
    useState<EforsyningIntegrationListItem | null>(null);

  const {
    data: integrations,
    isLoading,
    isFetching,
  } = useGetEforsyningMetadataIntegrationsEforsyningMetadataGetQuery(
    {
      accountId: Number.isFinite(accountIdAsNumber) ? accountIdAsNumber : null,
    },
    {
      skip: !Number.isFinite(accountIdAsNumber),
    },
  );

  const handleDeleteIntegration = (
    integration: EforsyningIntegrationListItem,
  ) => {
    setIntegrationToDelete(integration);
    setDeleteDialogOpen(true);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setIntegrationToDelete(null);
  };

  return (
    <>
      <LinearLoadingIndicator isLoading={isLoading || isFetching} />
      <PageContainer
        maxWidth="xl"
        sx={{ py: 2 }}
        data-testid="eforsyning-integration-overview-container"
      >
        <PageHeader
          title={t('overviewTitle', {
            defaultValue: 'Eforsyning Integrations',
          })}
          disableArrow
        >
          <WritableOnly>
            <SubmitButton
              component={Link}
              to="create"
              startIcon={<AddRoundedIcon />}
              data-testid="eforsyning-integration-add-button"
            >
              {t('newIntegration', {
                defaultValue: 'New integration',
              })}
            </SubmitButton>
          </WritableOnly>
        </PageHeader>

        <EforsyningIntegrationDataGrid
          integrations={integrations}
          isLoading={isLoading}
          onDeleteIntegration={handleDeleteIntegration}
        />

        <DeleteEforsyningIntegrationDialog
          open={deleteDialogOpen}
          integration={integrationToDelete}
          onClose={handleDeleteCancel}
        />
      </PageContainer>
    </>
  );
}
